import React, { useEffect, useMemo, useState } from "react";
import { PropTypes } from "prop-types";
import { useNavigate } from "react-router-dom";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import SiteNetworkHierarchy from "../../../../generics/SiteNetworkHierarchy";
import { format } from "date-fns";
import { useI18n } from "../../../../i18n/useI18n";
import apiService from "../../../../config/api-config";
import useReportApis from "../../commonHooks/useReportApis";
import { appsFilter } from "../../../../config/reqConfig/applications";
import { prepareQuery } from "../../../../utils/query-utils";
import {
  createObjFromEmailList,
  formatPageName,
  formatScope,
  getRandomString,
  isAllApplicationsTemplate,
  isAllSitesTemplate,
  isExecutiveTemplate,
  isLinkSummaryTemplate,
} from "../../../../utils/common";
import {
  APP,
  PAGE_NAME,
  SCHEDULE_FREQUENCY,
  regexEmail,
  regexName,
  SCHEDULE_TYPE,
  TEMPLATE_NAME,
  reportURLGeneratedReportsTab,
} from "../../../../utils/enums";
import {
  getTimePeriodELT,
  getTimeRepeats,
} from "../../../../utils/displayTime";
import { toast } from "../../../../utils/genericCommon";
import css from "../../reportsMagneticStyle.less";

const [
  HbrBanner,
  HbrKeyValuePairs,
  HbrButton,
  HbrCheckbox,
  HbrDrawer,
  HbrIcon,
  HbrInput,
  HbrOption,
  HbrRadio,
  HbrRadioGroup,
  HbrSelect,
  HbrDivider,
] = reactWrapper([
  "hbr-banner",
  "hbr-key-value-pairs",
  "hbr-button",
  "hbr-checkbox",
  "hbr-drawer",
  "hbr-icon",
  "hbr-input",
  "hbr-option",
  "hbr-radio",
  "hbr-radio-group",
  "hbr-select",
  "hbr-divider",
]);

const APP_SCOPE = {
  ALL: "all-application",
  CUSTOM: "custom-application",
};

const SITE_SCOPE = {
  ALL: "all-sites",
  CUSTOM: "custom-sites",
};

const API_ALL_APPLICATION = "all";

const getDefaultStartDate = () => format(new Date(), "yyyy-MM-dd");
const getDefaultStartTime = () => format(new Date(), "HH:mm");

const getLocalTimeFromUtc = (utcTime) => {
  if (!utcTime) return "";

  return new Date(`1970-01-01T${utcTime}Z`).toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  });
};
const getUtcTimeFromLocal = (localTime) => {
  if (!localTime || typeof localTime !== "string") return "";
  try {
    return new Date(`1970-01-01T${localTime}:00`)
      .toISOString()
      .substring(11, 19);
  } catch {
    return "";
  }
};
const getDateTimeParts = (timestamp) => {
  if (!timestamp) {
    return {
      startDate: getDefaultStartDate(),
      startTime: getDefaultStartTime(),
    };
  }

  return {
    startDate: format(new Date(timestamp), "yyyy-MM-dd"),
    startTime: format(new Date(timestamp), "HH:mm"),
  };
};

const createEmailGroupState = (list) => {
  const groupState = createObjFromEmailList(list);
  if (groupState?.textfieldgroup?.length) {
    return {
      textfieldgroup: groupState.textfieldgroup.map((item) => ({
        ...item,
        touched: false,
      })),
    };
  }

  return {
    textfieldgroup: [
      {
        name: "recipientEmail",
        validator: "email",
        validateon: "oninput",
        inputid: "recipientEmailId1",
        required: true,
        value: "",
        order: 0,
        errormsg: "",
        touched: false,
      },
    ],
  };
};

const getInitialFormState = (template) => {
  const fileTypes = (template?.file_type || "pdf")
    .split(",")
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);
  const defaultFileType = fileTypes.includes("pdf")
    ? "pdf"
    : fileTypes[0] || "pdf";

  return {
    appChoice: APP_SCOPE.ALL,
    emailGroupState: createEmailGroupState(""),
    fileType: defaultFileType,
    isSubmitting: false,
    repeats: SCHEDULE_FREQUENCY.DAILY,
    reportName: "",
    reportNameError: "",
    reportNameTouched: false,
    reportPasskey: getRandomString(10),
    scheduleMode: SCHEDULE_TYPE.ONCE,
    selectedApplication: APP.ALL,
    selectedSites: [],
    siteChoice: SITE_SCOPE.ALL,
    siteError: "",
    startDate: getDefaultStartDate(),
    startTime: getDefaultStartTime(),
    submitError: "",
    timeRange: SCHEDULE_FREQUENCY.DAILY,
  };
};
const applicationsOptions = [
  { label: "workflowAllApp", value: APP_SCOPE.ALL },
  { label: "workflowCustomApp", value: APP_SCOPE.CUSTOM },
];
const sitesOptions = [
  { label: "workflowAllSites", value: SITE_SCOPE.ALL },
  { label: "workflowAllCustomSite", value: SITE_SCOPE.CUSTOM },
];
const timeRangeOptions = [
  { label: "workflowLastTwentyFourhrs", value: SCHEDULE_FREQUENCY.DAILY },
  { label: "workflowLastWeek", value: SCHEDULE_FREQUENCY.WEEKLY },
  { label: "workflowLastMonth", value: SCHEDULE_FREQUENCY.MONTHLY },
];
const businessHoursDefault = {
  start: "09:00",
  end: "17:00",
};
const scheduleOptions = [
  { label: "workflowRunNow", value: SCHEDULE_TYPE.ONCE },
  { label: "workflowRunRecurring", value: SCHEDULE_TYPE.RECURRING },
];
const repeatOptions = [
  { label: "workflowDaily", value: SCHEDULE_FREQUENCY.DAILY },
  { label: "workflowWeekly", value: SCHEDULE_FREQUENCY.WEEKLY },
  { label: "workflowMonthly", value: SCHEDULE_FREQUENCY.MONTHLY },
];
const templatesScope = {
  [PAGE_NAME.EXECUTIVE]: {
    title: "reportTemplatesSummary",
    staticScope: ["workflowAllApp", "workflowAllSites"],
    allApplications: false,
    allSites: false,
  },
  [PAGE_NAME.ALL_APPLICATIONS]: {
    title: "reportTemplatesAppSum",
    allApplications: true,
    allSites: false,
  },
  [PAGE_NAME.ALL_SITES]: {
    title: "reportTemplatesSites",
    staticScope: ["workflowAllSites"],
    allApplications: false,
    allSites: false,
  },
  [PAGE_NAME.LINK_SLA]: {
    title: "linkSLALabel",
    allApplications: false,
    allSites: true,
  },
  [PAGE_NAME.LINK_UTILIZATION]: {
    title: "linkUtilizationLabel",
    allApplications: false,
    allSites: true,
  },
  [PAGE_NAME.LINK_AVAILABILITY]: {
    title: "linkAvailabilityLabel",
    allApplications: false,
    allSites: true,
  },
  [PAGE_NAME.LINK_SUMMARY]: {
    title: "linkSummaryLabel",
    allApplications: false,
    allSites: true,
  },
  [PAGE_NAME.TOP_CLIENTS]: {
    title: "reportPageTitleClientsSummary",
    allApplications: false,
    allSites: true,
  },
};
Object.values(templatesScope).forEach((item) => {
  const staticScope = [];
  if (item.allApplications === false) staticScope.push("workflowAllApp");
  if (item.allSites === false) staticScope.push("workflowAllSites");
  item.staticScope = staticScope;
});
const ReportTemplateDrawer = ({ onClose, onGenerated, report, template }) => {
  const { tViews } = useI18n("views");
  const navigate = useNavigate();
  const { state, getAppsDetails, getSitesDetails } = useReportApis();
  const [formState, setFormState] = useState(() =>
    getInitialFormState(template)
  );
  const [applicationOptions, setApplicationOptions] = useState([]);
  const [filteredApplicationOptions, setFilteredApplicationOptions] = useState(
    []
  );
  const [reportDetails, setReportDetails] = useState(null);
  const [businessHoursEnabled, setBusinessHoursEnabled] = useState(false);
  const [businessHoursStart, setBusinessHoursStart] = useState(
    businessHoursDefault.start
  );
  const [businessHoursEnd, setBusinessHoursEnd] = useState(
    businessHoursDefault.end
  );

  const isEditMode = Boolean(report?.report_uuid);
  const isOneTimeReport = report?.schedule_type === SCHEDULE_TYPE.ONCE;
  const isReadOnlyReport = isEditMode === true && isOneTimeReport === true;
  const emailIds = formState.emailGroupState?.textfieldgroup || [];

  const templateName = useMemo(() => {
    if (template) return template.template_name;
    else if (report) {
      const item = Object.entries(TEMPLATE_NAME).find(([, value]) => {
        return value === report.template_name;
      });
      if (item) return item[0];
    }
    return report?.template_name ?? "";
  }, []);

  const presentation = useMemo(() => {
    const item = templatesScope[templateName];
    return {
      ...item,
      scopeText: item?.staticScope.map((key) => tViews(key)).join(", ") ?? "",
      isStaticScope: item?.staticScope.length > 0,
    };
  }, [tViews]);

  useEffect(() => {
    if (report?.report_uuid) {
      apiService
        .getReportByUUID(report.report_uuid)
        .then((response) => {
          const details = response?.data?.[0] || null;
          setReportDetails(details);
        })
        .catch(() => {
          setReportDetails(null);
        });
      return;
    }

    if (presentation.allSites) {
      getSitesDetails();
    }

    if (presentation.allApplications) {
      getAppsDetails(prepareQuery(appsFilter, { timePeriodSelected: "720" }));
    }
  }, []);

  useEffect(() => {
    if (reportDetails) {
      const parsedScope = JSON.parse(reportDetails.scope || "{}");
      const businessHours = parsedScope.business_hours;
      const selectedSites = Array.isArray(parsedScope.site_id)
        ? parsedScope.site_id.filter((item) => item && item !== "all")
        : [];
      const selectedApplication = parsedScope.application?.[0] || "";
      const normalizedSelectedApplication =
        !selectedApplication || selectedApplication === API_ALL_APPLICATION
          ? APP.ALL
          : selectedApplication;
      const isRecurring =
        reportDetails.schedule_type === SCHEDULE_TYPE.RECURRING;
      const scheduleTimestamp = isRecurring
        ? reportDetails.repeat_time
        : reportDetails.next_run;
      const { startDate, startTime } = getDateTimeParts(scheduleTimestamp);

      setFormState({
        appChoice:
          selectedApplication &&
          selectedApplication !== APP.ALL &&
          selectedApplication !== API_ALL_APPLICATION
            ? APP_SCOPE.CUSTOM
            : APP_SCOPE.ALL,
        emailGroupState: createEmailGroupState(
          (reportDetails.recipient_email || "").split("|").join(",")
        ),
        fileType: (reportDetails.file_type || "pdf").toLowerCase(),
        isSubmitting: false,
        repeats: reportDetails.schedule_freq || SCHEDULE_FREQUENCY.DAILY,
        reportName: reportDetails.report_name || "",
        reportNameError: "",
        reportNameTouched: false,
        reportPasskey: reportDetails.report_passkey || getRandomString(10),
        scheduleMode: isRecurring
          ? SCHEDULE_TYPE.RECURRING
          : SCHEDULE_TYPE.ONCE,
        selectedApplication: normalizedSelectedApplication,
        selectedSites,
        siteChoice:
          selectedSites.length > 0 ? SITE_SCOPE.CUSTOM : SITE_SCOPE.ALL,
        siteError: "",
        startDate,
        startTime,
        submitError: "",
        timeRange: reportDetails.time_range || SCHEDULE_FREQUENCY.DAILY,
      });
      setBusinessHoursEnabled(Boolean(businessHours));
      setBusinessHoursStart(
        getLocalTimeFromUtc(businessHours?.start) || businessHoursDefault.start
      );
      setBusinessHoursEnd(
        getLocalTimeFromUtc(businessHours?.end) || businessHoursDefault.end
      );

      if (presentation.allSites) {
        getSitesDetails();
      }

      if (presentation.allApplications) {
        getAppsDetails(prepareQuery(appsFilter, { timePeriodSelected: "720" }));
      }
    }
  }, [reportDetails]);

  useEffect(() => {
    if (!Array.isArray(state.appsDetails)) {
      return;
    }

    const appMap = state.appsDetails.reduce((acc, item) => {
      if (!item?.application) return acc;
      acc[item.application] = {
        text: item.application,
        value: item.application,
      };
      return acc;
    }, {});

    const apps = Object.values(appMap);
    setApplicationOptions(apps);
    setFilteredApplicationOptions(apps);
  }, [state.appsDetails]);

  const sites = useMemo(() => {
    if (!Array.isArray(state.sitesDetails)) {
      return [];
    }

    return state.sitesDetails.map((item) => ({
      text: item.site_name,
      value: item.site_id,
      hierarchy_path: item.hierarchy_path,
      site_id: item.site_id,
      site_name: item.site_name,
    }));
  }, [state.sitesDetails]);

  const fileTypeOptions = useMemo(() => {
    return (
      template?.file_type ||
      reportDetails?.file_type ||
      report?.file_type ||
      "pdf"
    )
      .split(",")
      .map((item) => item.trim().toLowerCase())
      .filter(Boolean);
  }, [report, reportDetails, template]);
  const hasSingleFileTypeOption = fileTypeOptions.length === 1;
  const staticKeyValuePairs = useMemo(() => {
    const items = [];

    if (presentation.isStaticScope) {
      items.push({
        id: "scope",
        label: tViews("workflowScope"),
        value: presentation.scopeText,
      });
    }

    if (hasSingleFileTypeOption) {
      items.push({
        id: "file",
        label: tViews("workflowFile"),
        value: formState.fileType.toUpperCase(),
      });
    }

    return items;
  }, [tViews]);

  const emailValues = useMemo(() => {
    return emailIds.map((item) => item.value?.trim()).filter(Boolean);
  }, [formState.emailGroupState]);

  const hasDuplicateEmails = useMemo(() => {
    const lowered = emailValues.map((item) => item.toLowerCase());
    return new Set(lowered).size !== lowered.length;
  }, [emailValues]);

  const hasInvalidEmail = useMemo(() => {
    return emailValues.some(
      (email) => !regexEmail.test(String(email).toLowerCase())
    );
  }, [emailValues]);

  const getEmailErrorMessage = (field, fieldIndex) => {
    const emailValue = field.value?.trim() || "";
    const normalizedEmailValue = emailValue.toLowerCase();
    const isDuplicateEmail =
      normalizedEmailValue &&
      emailIds.some(
        (email, index) =>
          index !== fieldIndex &&
          email.value?.trim().toLowerCase() === normalizedEmailValue
      );

    if (isDuplicateEmail) {
      return tViews("duplicateEmailMsg");
    }

    if (!field.touched) {
      return "";
    }

    if (!emailValue) {
      return tViews("workflowReportNameRequired");
    }

    if (!regexEmail.test(normalizedEmailValue)) {
      return tViews("predictiveNetworks.invalidEmailMsg");
    }

    return "";
  };
  const showBusinessHoursSection =
    isLinkSummaryTemplate(templateName) &&
    formState.timeRange === SCHEDULE_FREQUENCY.DAILY;

  const isFormValid = () => {
    if (!formState.reportName.trim()) return false;
    if (!regexName.test(String(formState.reportName).toLowerCase()))
      return false;
    if (
      formState.siteChoice === SITE_SCOPE.CUSTOM &&
      formState.selectedSites.length === 0
    )
      return false;
    if (
      formState.appChoice === APP_SCOPE.CUSTOM &&
      !formState.selectedApplication
    )
      return false;
    if (
      emailIds.length === 0 ||
      emailIds.some((field) => !field.value?.trim()) ||
      hasDuplicateEmails ||
      hasInvalidEmail
    )
      return false;
    if (
      formState.scheduleMode === SCHEDULE_TYPE.RECURRING &&
      !formState.startTime
    )
      return false;
    return true;
  };

  const normalizeScopeForSubmission = (scopePayload) => {
    const formattedScope = formatScope(scopePayload, templateName);

    if (
      ![
        PAGE_NAME.LINK_SLA,
        PAGE_NAME.LINK_UTILIZATION,
        PAGE_NAME.LINK_AVAILABILITY,
        PAGE_NAME.LINK_SUMMARY,
        PAGE_NAME.TOP_CLIENTS,
      ].includes(templateName)
    ) {
      return formattedScope;
    }

    try {
      const parsedScope = JSON.parse(formattedScope);
      const isSiteEmpty =
        !Array.isArray(parsedScope.site_id) ||
        parsedScope.site_id.length === 0 ||
        parsedScope.site_id.every((site) => !site);

      if (isSiteEmpty) {
        parsedScope.site_id = ["all"];
      }

      return JSON.stringify(parsedScope);
    } catch (error) {
      return formattedScope;
    }
  };

  const updateFormState = (patch) => {
    setFormState((prev) => ({ ...prev, ...patch }));
  };

  const getScopePayload = () => {
    if (isExecutiveTemplate(templateName)) {
      return { site_id: "", application: "" };
    }

    if (isAllApplicationsTemplate(templateName)) {
      return {
        site_id: "",
        application:
          formState.appChoice === APP_SCOPE.CUSTOM
            ? formState.selectedApplication
            : API_ALL_APPLICATION,
      };
    }

    if (isAllSitesTemplate(templateName)) {
      return {
        site_id: "all",
        application: "",
      };
    }

    return {
      site_id:
        formState.siteChoice === SITE_SCOPE.CUSTOM
          ? formState.selectedSites
          : "all",
      application: "",
      ...(showBusinessHoursSection && businessHoursEnabled
        ? {
            business_hours: {
              start: getUtcTimeFromLocal(businessHoursStart),
              end: getUtcTimeFromLocal(businessHoursEnd),
            },
          }
        : {}),
    };
  };

  const buildScheduleValues = () => {
    const selectedDateTime = new Date(
      `${formState.startDate}T${formState.startTime}:00`
    );
    const baseTime = selectedDateTime.getTime();

    if (formState.scheduleMode === SCHEDULE_TYPE.ONCE) {
      const currTime = new Date().setSeconds(0, 0);
      return {
        nextRun: currTime,
        repeatTime: currTime,
        repeatOn: -1,
        scheduleFreq: SCHEDULE_FREQUENCY.NOW,
        scheduleType: SCHEDULE_TYPE.ONCE,
      };
    }

    return {
      nextRun: baseTime,
      repeatTime: baseTime,
      repeatOn: 1,
      scheduleFreq: formState.repeats,
      scheduleType: SCHEDULE_TYPE.RECURRING,
    };
  };

  const closeDrawer = () => {
    updateFormState({
      reportNameError: "",
      siteError: "",
      submitError: "",
    });
    onClose();
  };

  const handleClose = (event) => {
    if (event && event.target?.id !== "report-template-drawer") {
      return;
    }

    closeDrawer();
  };

  const handleEmailChange = (index, value) => {
    const nextGroup = [...emailIds];
    if (nextGroup[index]) {
      nextGroup[index] = {
        ...nextGroup[index],
        value,
        touched: true,
        errormsg: "",
      };
    }

    updateFormState({
      emailGroupState: { textfieldgroup: nextGroup },
      submitError: "",
    });
  };

  const handleEmailBlur = (index) => {
    const nextGroup = [...emailIds];
    if (!nextGroup[index]) {
      return;
    }

    nextGroup[index] = {
      ...nextGroup[index],
      touched: true,
    };

    updateFormState({
      emailGroupState: { textfieldgroup: nextGroup },
    });
  };

  const handleEmailAdd = () => {
    const nextGroup = [...emailIds];
    const nextIndex = nextGroup.length + 1;
    nextGroup.push({
      name: "recipientEmail",
      validator: "email",
      validateon: "oninput",
      inputid: `recipientEmailId${nextIndex}`,
      required: true,
      value: "",
      order: nextIndex - 1,
      errormsg: "",
      touched: false,
    });

    updateFormState({
      emailGroupState: { textfieldgroup: nextGroup },
    });

    requestAnimationFrame(() => {
      document
        .getElementById(`recipientEmailId${nextIndex}`)
        ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  };

  const handleEmailClose = (pos) => {
    const nextGroup = [...emailIds];
    nextGroup.splice(pos, 1);

    const normalized = nextGroup.map((input, index) => ({
      ...input,
      inputid: `recipientEmailId${index + 1}`,
      order: index,
    }));

    updateFormState({
      emailGroupState: {
        textfieldgroup: normalized.length
          ? normalized
          : createEmailGroupState("").textfieldgroup,
      },
    });
  };

  const handleAppSearch = (event) => {
    const searchText = event.detail?.value?.toLowerCase() || "";
    const filtered = applicationOptions.filter((option) =>
      option.text.toLowerCase().includes(searchText)
    );
    setFilteredApplicationOptions(filtered);
  };

  const renderStaticValueSection = (label, value) => (
    <HbrKeyValuePairs direction="row">
      <dl>
        <dt>{label}</dt>
        <dd>{value}</dd>
      </dl>
    </HbrKeyValuePairs>
  );

  const getApplicationDisplayValue = () => {
    if (!presentation.allApplications || isExecutiveTemplate(templateName)) {
      return "";
    }

    return formState.appChoice === APP_SCOPE.CUSTOM
      ? formState.selectedApplication || "-"
      : tViews("workflowAllApp");
  };

  const getScopeDisplayValue = () => {
    if (isExecutiveTemplate(templateName)) {
      return `${tViews("workflowAllApp")}, ${tViews("workflowAllSites")}`;
    }

    if (isAllApplicationsTemplate(templateName)) {
      return getApplicationDisplayValue();
    }

    if (
      isAllSitesTemplate(templateName) ||
      formState.siteChoice === SITE_SCOPE.ALL
    ) {
      return tViews("workflowAllSites");
    }

    const selectedSiteIds = formState.selectedSites
      .map((siteId) => String(siteId))
      .filter(Boolean);
    const selectedSiteNames = sites
      .filter((site) => selectedSiteIds.includes(String(site.value)))
      .map((site) => site.text);

    if (selectedSiteNames.length) {
      return selectedSiteNames.join(", ");
    }

    return selectedSiteIds.length ? selectedSiteIds.join(", ") : "-";
  };

  const getTimeRangeDisplayValue = () => {
    switch (formState.timeRange) {
      case SCHEDULE_FREQUENCY.WEEKLY:
        return tViews("workflowLastWeek");
      case SCHEDULE_FREQUENCY.MONTHLY:
        return tViews("workflowLastMonth");
      default:
        return tViews("workflowLastTwentyFourhrs");
    }
  };

  const getScheduleDisplayValue = () => {
    if (formState.scheduleMode === SCHEDULE_TYPE.ONCE) {
      return tViews("workflowRunNow");
    }

    const repeatsLabel =
      formState.repeats === SCHEDULE_FREQUENCY.WEEKLY
        ? tViews("workflowWeekly")
        : formState.repeats === SCHEDULE_FREQUENCY.MONTHLY
        ? tViews("workflowMonthly")
        : tViews("workflowDaily");

    return `${tViews("workflowRunRecurring")} - ${repeatsLabel}${
      formState.startTime ? `, ${formState.startTime}` : ""
    }`;
  };

  const getBusinessHoursDisplayValue = () => {
    if (!showBusinessHoursSection) {
      return "";
    }

    if (!businessHoursEnabled) {
      return tViews("workflowOff");
    }

    return `${businessHoursStart} - ${businessHoursEnd}`;
  };

  const getReportNameErrorMessage = () => {
    if (formState.reportNameTouched && !formState.reportName.trim()) {
      return tViews("workflowReportNameRequired");
    }

    if (
      formState.reportNameTouched &&
      formState.reportName.trim() &&
      !regexName.test(String(formState.reportName).toLowerCase())
    ) {
      return tViews("workflowEmptyReportNameErrorRegexName");
    }

    if (formState.reportNameError === tViews("workflowNext")) {
      return "";
    }

    return formState.reportNameError;
  };

  const validateReportName = async (reportName) => {
    const normalizedReportName = reportName.trim();

    if (
      !normalizedReportName ||
      !regexName.test(String(normalizedReportName).toLowerCase())
    ) {
      return true;
    }

    const overlayId = localStorage.getItem("currentOverlay");

    try {
      const isSameNameAsExisting =
        isEditMode &&
        reportDetails &&
        reportDetails.report_name?.trim() === normalizedReportName;
      const existingName = await apiService.validateReportName(
        normalizedReportName,
        overlayId
      );
      const isDuplicateName =
        !isSameNameAsExisting &&
        Array.isArray(existingName?.data) &&
        existingName.data.length > 0;

      setFormState((prev) => {
        if (prev.reportName.trim() !== normalizedReportName) {
          return prev;
        }
        return {
          ...prev,
          reportNameError: isDuplicateName ? tViews("workflowNext") : "",
        };
      });

      return !isDuplicateName;
    } catch (error) {
      return true;
    }
  };

  const handleSubmit = async () => {
    const normalizedReportName = formState.reportName.trim();
    const nextEmailGroup = emailIds.map((field) => ({
      ...field,
      touched: true,
    }));

    updateFormState({
      emailGroupState: { textfieldgroup: nextEmailGroup },
      reportName: normalizedReportName,
      reportNameTouched: true,
      reportNameError: "",
      siteError: "",
      submitError: "",
    });

    if (!isFormValid()) {
      updateFormState({
        siteError:
          formState.siteChoice === SITE_SCOPE.CUSTOM &&
          formState.selectedSites.length === 0
            ? tViews("workflowErrorMsgSite")
            : "",
      });
      return;
    }

    const overlayId = localStorage.getItem("currentOverlay");
    const overlayName = localStorage.getItem("currentOverlayName");
    const localOffset = `${new Date().getTimezoneOffset()}_${new Date().getTimezoneOffset()}`;

    updateFormState({ isSubmitting: true });

    try {
      const isReportNameAvailable = await validateReportName(
        normalizedReportName
      );
      if (!isReportNameAvailable) {
        updateFormState({
          isSubmitting: false,
          reportNameTouched: true,
        });
        return;
      }

      const scope = getScopePayload();
      const schedule = buildScheduleValues();
      const timeFilter = getTimePeriodELT(
        formState.timeRange,
        formState.scheduleMode === SCHEDULE_TYPE.RECURRING
          ? getTimeRepeats(formState.repeats, schedule.repeatTime)
          : schedule.nextRun
      );
      const recipientEmail = emailValues.join("|");
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const formattedScope = normalizeScopeForSubmission(scope);

      const payload = {
        update_field: {
          report_name: normalizedReportName,
          file_type: formState.fileType,
          schedule_type: schedule.scheduleType,
          schedule_freq: schedule.scheduleFreq,
          repeat_on: schedule.repeatOn,
          repeat_time: schedule.repeatTime,
          template_name: TEMPLATE_NAME[templateName],
          recipient_email: recipientEmail,
          delivery_type: "attachment",
          template_uuid:
            template?.template_uuid ||
            reportDetails?.template_uuid ||
            report?.template_uuid,
          created_by: `ui_${localOffset}`,
          schedule_status: "pending",
          overlay_id: overlayId,
          overlay_name: overlayName,
          time_range_type: timeZone,
          current_period_start: timeFilter.current_period[0],
          current_period_end: timeFilter.current_period[1],
          previous_period_start: timeFilter.previous_period[0],
          previous_period_end: timeFilter.previous_period[1],
          scope: formattedScope,
          status: "active",
          next_run: schedule.nextRun,
          page_name: formatPageName(templateName, formState.fileType, scope),
          time_range: formState.timeRange,
          report_passkey: formState.reportPasskey,
        },
      };

      const result = isEditMode
        ? await apiService.editReport({
            update_field: {
              recipient_email: recipientEmail,
              updated_time: Date.now(),
              updated_by: `ui_${localOffset}`,
            },
            query: {
              rules: {
                report_uuid: reportDetails?.report_uuid || report?.report_uuid,
              },
            },
          })
        : await apiService.generateReport(payload, overlayId);

      if (result?.data) {
        toast(tViews("reportSubmitSuccessMsg"), "success", 2500, "", {
          className: "report-drawer-toast",
        });
        updateFormState({
          isSubmitting: false,
        });
        onGenerated?.();
        onClose();
        navigate(reportURLGeneratedReportsTab);
        return;
      }

      updateFormState({
        isSubmitting: false,
        submitError: tViews("workflowErrorSubmitting"),
      });
    } catch (error) {
      updateFormState({
        isSubmitting: false,
        submitError: tViews("workflowErrorSubmitting"),
      });
    }
  };

  if (!templateName) {
    return null;
  }

  return (
    <div className={css["report-template-drawer"]}>
      <HbrDrawer
        id="report-template-drawer"
        label={presentation.title ? tViews(presentation.title) : templateName}
        open
        overlay
        onHbr-hide={handleClose}
        onHbr-overlay-dismiss={(event) => {
          event.preventDefault();
        }}
      >
        <div className="hbr-css__layout-col-lg">
          {formState.reportNameError === tViews("workflowNext") && (
            <HbrBanner open sentiment="danger" variant="fill">
              <HbrIcon slot="icon" name="alerts-negative-large" />
              {formState.reportNameError}
            </HbrBanner>
          )}
          {isEditMode === false && staticKeyValuePairs.length > 0 && (
            <HbrKeyValuePairs direction="row">
              <dl>
                {staticKeyValuePairs.map((item) => (
                  <React.Fragment key={item.id}>
                    <dt>{item.label}</dt>
                    <dd>{item.value}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </HbrKeyValuePairs>
          )}

          {formState.submitError && (
            <HbrBanner open sentiment="danger" variant="fill">
              <HbrIcon slot="icon" name="alerts-negative-large" />
              {formState.submitError}
            </HbrBanner>
          )}
          <HbrInput
            data-testid="report-name-input"
            label={tViews("workflowReportNameLabel")}
            placeholder={tViews("workflowReportNameLabel")}
            value={formState.reportName}
            maxlength={32}
            disabled={isEditMode}
            onHbr-input={(event) => {
              updateFormState({
                reportName: event.target.value,
                reportNameError: "",
                submitError: "",
              });
            }}
            onBlur={(event) => {
              const normalizedReportName = (
                event?.target?.value || formState.reportName
              ).trim();
              updateFormState({
                reportName: normalizedReportName,
                reportNameTouched: true,
              });
              validateReportName(normalizedReportName);
            }}
          >
            {getReportNameErrorMessage() && (
              <div slot="help-text" className="field-error">
                {getReportNameErrorMessage()}
              </div>
            )}
          </HbrInput>

          {presentation.allSites === true &&
            (isEditMode === true ? (
              renderStaticValueSection(
                tViews("workflowSite"),
                getScopeDisplayValue()
              )
            ) : (
              <div className="hbr-css__layout-col-md">
                <HbrRadioGroup
                  name="application-scope"
                  direction="column"
                  label={tViews("workflowSite")}
                  onHbr-click={(event) => {
                    const value = event.target.value;
                    const result = {
                      siteChoice: value,
                      siteError: "",
                    };
                    if (value === SITE_SCOPE.ALL) result.selectedSites = [];
                    updateFormState(result);
                  }}
                >
                  {sitesOptions.map((item) => (
                    <HbrRadio
                      key={item.value}
                      value={item.value}
                      checked={formState.siteChoice === item.value}
                    >
                      {tViews(item.label)}
                    </HbrRadio>
                  ))}
                </HbrRadioGroup>
                {formState.siteChoice === SITE_SCOPE.CUSTOM && (
                  <div className="hbr-css__layout-col-xs">
                    <SiteNetworkHierarchy
                      isReport
                      closeOnSelect={false}
                      globalFilter={{ sites, selectedSite: -1 }}
                      sites={sites}
                      site={formState.selectedSites}
                      handleSiteSelect={(selected) => {
                        const list = Array.isArray(selected)
                          ? selected
                          : [selected];
                        updateFormState({
                          selectedSites: list.filter(Boolean),
                          siteError: "",
                        });
                      }}
                    />
                    {formState.siteError && (
                      <div className="field-error">{formState.siteError}</div>
                    )}
                  </div>
                )}
              </div>
            ))}

          {presentation.allApplications === true &&
            (isEditMode === true ? (
              renderStaticValueSection(
                tViews("workflowApplication"),
                getApplicationDisplayValue()
              )
            ) : (
              <div className="hbr-css__layout-col-md">
                <HbrRadioGroup
                  name="application-scope"
                  direction="column"
                  label={tViews("workflowApplication")}
                  onHbr-click={(event) => {
                    const value = event.target.value;
                    updateFormState({
                      appChoice: value,
                      selectedApplication:
                        value === APP_SCOPE.ALL ? APP_SCOPE.ALL : "",
                    });
                  }}
                >
                  {applicationsOptions.map((item) => (
                    <HbrRadio
                      key={item.value}
                      value={item.value}
                      checked={formState.appChoice === item.value}
                    >
                      {tViews(item.label)}
                    </HbrRadio>
                  ))}
                </HbrRadioGroup>
                {formState.appChoice === APP_SCOPE.CUSTOM && (
                  <HbrSelect
                    searchable
                    placeholder={tViews("workflowEnterApp")}
                    value={formState.selectedApplication}
                    onHbr-search={handleAppSearch}
                    onHbr-change={(event) => {
                      updateFormState({
                        selectedApplication: event.detail?.option?.value || "",
                      });
                    }}
                  >
                    {filteredApplicationOptions.map((option) => (
                      <HbrOption key={option.value} value={option.value}>
                        {option.text}
                      </HbrOption>
                    ))}
                  </HbrSelect>
                )}
              </div>
            ))}

          {isEditMode === true
            ? renderStaticValueSection(
                tViews("workflowFile"),
                formState.fileType.toUpperCase()
              )
            : hasSingleFileTypeOption === false && (
                <HbrRadioGroup
                  name="file-type"
                  direction="column"
                  label={tViews("workflowFile")}
                  onHbr-click={(event) => {
                    updateFormState({ fileType: event.target.value });
                  }}
                >
                  {fileTypeOptions.map((item) => (
                    <HbrRadio
                      key={item}
                      value={item}
                      checked={formState.fileType === item}
                    >
                      {item.toUpperCase()}
                    </HbrRadio>
                  ))}
                </HbrRadioGroup>
              )}

          {isEditMode === true ? (
            renderStaticValueSection(
              tViews("workflowTimeRange"),
              getTimeRangeDisplayValue()
            )
          ) : (
            <HbrRadioGroup
              name="time-range"
              direction="column"
              label={tViews("workflowTimeRange")}
              onHbr-click={(event) => {
                const value = event.target.value;
                updateFormState({ timeRange: value });
                setBusinessHoursEnabled(value === SCHEDULE_FREQUENCY.DAILY);
              }}
            >
              {timeRangeOptions.map((item) => (
                <HbrRadio
                  key={item.value}
                  value={item.value}
                  checked={formState.timeRange === item.value}
                >
                  {tViews(item.label)}
                </HbrRadio>
              ))}
            </HbrRadioGroup>
          )}

          {showBusinessHoursSection &&
            (isEditMode ? (
              renderStaticValueSection(
                tViews("workflowBusinessHours"),
                getBusinessHoursDisplayValue()
              )
            ) : (
              <div className="hbr-css__layout-col-md">
                <HbrCheckbox
                  name="businessHours"
                  className="business-hours-checkbox"
                  checked={businessHoursEnabled}
                  onHbr-change={(event) =>
                    setBusinessHoursEnabled(event.target.checked)
                  }
                >
                  {tViews("workflowBusinessHours")}
                </HbrCheckbox>
                {businessHoursEnabled && (
                  <div className="hbr-css__layout-row-sm">
                    <HbrInput
                      type="time"
                      name="businessHoursStart"
                      label={tViews("workflowStart")}
                      value={businessHoursStart}
                      onHbr-input={(event) =>
                        setBusinessHoursStart(event.target.value)
                      }
                    />
                    <HbrInput
                      type="time"
                      name="businessHoursEnd"
                      label={tViews("workflowEnd")}
                      value={businessHoursEnd}
                      onHbr-input={(event) =>
                        setBusinessHoursEnd(event.target.value)
                      }
                    />
                  </div>
                )}
              </div>
            ))}

          {isEditMode ? (
            renderStaticValueSection(
              tViews("workflowSchedule"),
              getScheduleDisplayValue()
            )
          ) : (
            <div className="hbr-css__layout-col-md">
              <HbrRadioGroup
                name="schedule-mode"
                direction="column"
                label={tViews("workflowSchedule")}
                onHbr-click={(event) =>
                  updateFormState({ scheduleMode: event.target.value })
                }
              >
                {scheduleOptions.map((item) => (
                  <HbrRadio
                    key={item.value}
                    value={item.value}
                    checked={formState.scheduleMode === item.value}
                  >
                    {tViews(item.label)}
                  </HbrRadio>
                ))}
              </HbrRadioGroup>

              {formState.scheduleMode === SCHEDULE_TYPE.RECURRING && (
                <>
                  <HbrDivider noPadding />
                  <div className="hbr-css__layout-col-md">
                    <HbrSelect
                      label={tViews("workflowRepeats")}
                      value={formState.repeats}
                      onHbr-change={(event) => {
                        updateFormState({ repeats: event.target.value });
                      }}
                    >
                      {repeatOptions.map((item) => (
                        <HbrOption key={item.value} value={item.value}>
                          {tViews(item.label)}
                        </HbrOption>
                      ))}
                    </HbrSelect>
                    <HbrInput
                      label={tViews("workflowStart")}
                      type="time"
                      value={formState.startTime}
                      onHbr-input={(event) =>
                        updateFormState({ startTime: event.target.value })
                      }
                    />
                  </div>
                </>
              )}
            </div>
          )}

          <div data-cy="copyActions">
            <HbrInput
              readonly
              togglePassword
              type="password"
              label={tViews("workflowDeliveryDocumentKey")}
              value={formState.reportPasskey}
            >
              <HbrButton
                slot="suffix"
                variant="text"
                aria-label={tViews("a11yLabels.workflowCopyPasskey")}
                title={tViews("a11yLabels.workflowCopyPasskey")}
                onClick={() => {
                  navigator.clipboard.writeText(formState.reportPasskey);
                  toast(tViews("workflowSuccessMessage"), "success", 5000, "", {
                    className: "report-drawer-toast",
                  });
                }}
              >
                <HbrIcon name="copy" />
              </HbrButton>
            </HbrInput>
          </div>

          <div>
            {emailIds.map((item, index) => {
              const emailErrorMessage = getEmailErrorMessage(item, index);
              return (
                <div
                  key={item.inputid}
                  className="hbr-css__layout-row-xs hbr-css__layout-align-center"
                >
                  <HbrInput
                    id={item.inputid}
                    label={tViews("workflowEmailRecipient")}
                    type="email"
                    required
                    placeholder={`${tViews("workflowEmail")} ${index + 1}`}
                    value={item.value}
                    disabled={isReadOnlyReport}
                    error={Boolean(emailErrorMessage)}
                    onHbr-input={(event) => {
                      handleEmailChange(index, event.target.value || "");
                    }}
                    onBlur={() => handleEmailBlur(index)}
                  >
                    <div slot="help-text" className="field-error">
                      {emailErrorMessage}&nbsp;
                    </div>
                  </HbrInput>
                  {isReadOnlyReport === false && emailIds.length > 1 && (
                    <HbrButton
                      variant="text"
                      aria-label={tViews("workflowDelete")}
                      title={tViews("workflowDelete")}
                      onClick={() => handleEmailClose(index)}
                    >
                      <HbrIcon name="trash-simple" />
                    </HbrButton>
                  )}
                  {isReadOnlyReport === false &&
                    emailIds.length < 5 &&
                    index === emailIds.length - 1 && (
                      <HbrButton
                        variant="text"
                        aria-label={tViews("workflowAdd")}
                        title={tViews("workflowAdd")}
                        onClick={handleEmailAdd}
                      >
                        <HbrIcon name="plus" />
                      </HbrButton>
                    )}
                </div>
              );
            })}
          </div>
        </div>

        <div slot="footer">
          <div className="hbr-css__button-group hbr-css__button-group-raised hbr-css__button-group-end">
            <HbrButton variant="text" onClick={closeDrawer}>
              {tViews("workflowCancel")}
            </HbrButton>
            {isReadOnlyReport === false && (
              <HbrButton
                variant="fill"
                disabled={!isFormValid() || formState.isSubmitting}
                onClick={handleSubmit}
              >
                {formState.isSubmitting
                  ? tViews("workflowSubmitting")
                  : tViews("workflowGenerateReport")}
              </HbrButton>
            )}
          </div>
        </div>
      </HbrDrawer>
    </div>
  );
};

ReportTemplateDrawer.propTypes = {
  onClose: PropTypes.func.isRequired,
  onGenerated: PropTypes.func.isRequired,
  report: PropTypes.object,
  template: PropTypes.object,
};

export default ReportTemplateDrawer;
